import { useEffect, useState } from "react";
import api, { apiError, fileUrl } from "@/lib/api";
import { NUM, pname } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ClipboardEdit, ScanLine, Package, X, Loader2, CheckCircle2, Warehouse, Store, ArrowRight, CloudCheck, CloudOff } from "lucide-react";
import { toast } from "sonner";
import BarcodeInput from "@/components/BarcodeInput";
import BarcodeScanner from "@/components/BarcodeScanner";
import ProductPicker from "@/components/ProductPicker";
import { confirm } from "@/components/ConfirmDialog";
import { magOf, depoOf } from "@/components/StockSplit";

const TYPES = [
  ["sayim", "Sayım", "Bölümdeki gerçek adet", "border-primary bg-primary/5 ring-primary"],
  ["giris", "Giriş", "Bölüme eklenecek", "border-emerald-500 bg-emerald-50 ring-emerald-500"],
  ["cikis", "Çıkış", "Bölümden düşülecek", "border-red-500 bg-red-50 ring-red-500"],
  ["transfer", "Aktarma", "Depo ↔ Mağaza", "border-sky-500 bg-sky-50 ring-sky-500"],
];
const LOC_TR = { depo: "Depo", magaza: "Mağaza" };
const QTY_LABEL = { sayim: "Sayılan", giris: "Eklenecek", cikis: "Düşülecek", transfer: "Aktarılacak" };
const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const curAt = (p, loc) => (loc === "magaza" ? magOf(p) : depoOf(p));
const qDefault = (p, t, loc) => (t === "sayim" ? (curAt(p, loc) || "") : 1);   // sayımda 0 stok boş görünür
// Hareket sonrası Depo/Mağaza (backend ile aynı kural; transfer: loc = hedef bölüm)
const afterMove = (p, t, loc, q) => {
  const m = magOf(p), d = depoOf(p);
  if (t === "transfer") return loc === "magaza" ? { d: r2(d - q), m: r2(m + q) } : { d: r2(d + q), m: r2(m - q) };
  if (loc === "magaza") return { d, m: r2(t === "sayim" ? q : t === "cikis" ? m - q : m + q) };
  return { d: r2(t === "sayim" ? q : t === "cikis" ? d - q : d + q), m };
};
const snap = (p) => ({ id: p.id, name: p.name, code: p.code || "", brand: p.brand || "", unit: p.unit || "ADET", image_url: p.image_url || (p.images || [])[0] || "" });

function LocPicker({ type, loc, onPick }) {
  const opts = type === "transfer"
    ? [["magaza", "Depo → Mağaza", Store], ["depo", "Mağaza → Depo", Warehouse]]
    : [["depo", "Depo", Warehouse], ["magaza", "Satış Mağazası", Store]];
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="quick-loc">
      {opts.map(([v, lbl, Icon]) => (
        <button key={v} type="button" onClick={() => onPick(v)} data-testid={`quick-loc-${v}`}
          className={cn("rounded-xl border px-3 py-2 flex items-center gap-2 text-sm font-bold transition-colors", loc === v ? (v === "magaza" ? "border-teal-500 bg-teal-50 text-teal-800 ring-1 ring-teal-500" : "border-slate-500 bg-slate-100 text-slate-800 ring-1 ring-slate-500") : "hover:bg-muted/50 text-muted-foreground")}>
          <Icon className="h-4 w-4" />{lbl}
        </button>
      ))}
    </div>
  );
}

function QuickRow({ x, type, loc, onQty, onRemove }) {
  const p = x.product;
  const img = p.image_url || (p.images || [])[0];
  const after = afterMove(p, type, loc, Number(x.qty) || 0);
  const cell = (lbl, a, b, hi) => (
    <span className={cn("inline-flex items-center gap-1 rounded px-1.5", hi ? "bg-primary/10" : "")}>
      <span className="text-[9px] font-black text-muted-foreground">{lbl}</span><span className="tabular-nums">{NUM(a)}</span>
      {a !== b && <><ArrowRight className="h-3 w-3 text-muted-foreground" /><b className={cn("tabular-nums", b < 0 ? "text-destructive" : "text-[#16A34A]")}>{NUM(b)}</b></>}
    </span>
  );
  return (
    <div className="flex items-center gap-2 sm:gap-3 rounded-xl border p-2 sm:p-2.5 bg-card" data-testid={`quick-item-${p.id}`}>
      {img ? <img src={fileUrl(img)} alt="" className="hidden sm:block h-12 w-12 rounded-lg object-contain bg-white border shrink-0" /> : <div className="hidden sm:flex h-12 w-12 rounded-lg bg-muted items-center justify-center shrink-0"><Package className="h-5 w-5 text-muted-foreground/40" /></div>}
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-sm leading-snug break-words" data-testid={`quick-name-${p.id}`}>{pname(p)}</div>
        {p.code && <div className="text-[11px] text-muted-foreground font-mono truncate">{p.code}</div>}
        <div className="text-xs flex flex-wrap items-center gap-x-1.5 gap-y-0.5 mt-0.5" data-testid={`quick-after-${p.id}`}>
          {cell("DEPO", depoOf(p), after.d, loc === "depo" || type === "transfer")}
          {cell("MAĞAZA", magOf(p), after.m, loc === "magaza" || type === "transfer")}
          <span className="text-muted-foreground">· Toplam <b className="text-foreground tabular-nums">{NUM(r2(after.d + after.m))}</b> {p.unit}</span>
        </div>
      </div>
      <div className="shrink-0 w-20 sm:w-28">
        <div className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">{QTY_LABEL[type]}</div>
        <Input type="number" inputMode="decimal" className="h-9 text-right tabular-nums" placeholder="adet" value={x.qty === 0 ? "" : x.qty} onChange={(e) => onQty(p.id, e.target.value)} data-testid={`quick-qty-${p.id}`} />
      </div>
      <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => onRemove(p.id)} data-testid={`quick-rm-${p.id}`}><X className="h-4 w-4 text-destructive" /></Button>
    </div>
  );
}

function SaveState({ state }) {
  if (!state) return null;
  if (state === "error") return <span className="text-[11px] font-semibold text-destructive inline-flex items-center gap-1" data-testid="quick-draft-state"><CloudOff className="h-3.5 w-3.5" />Taslak kaydedilemedi</span>;
  return (
    <span className="text-[11px] font-semibold text-muted-foreground inline-flex items-center gap-1" data-testid="quick-draft-state">
      {state === "saving" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CloudCheck className="h-3.5 w-3.5 text-[#16A34A]" />}
      {state === "saving" ? "Kaydediliyor…" : "Taslak otomatik kaydedildi"}
    </span>
  );
}

/** Hızlı Sayım / Giriş / Çıkış / Aktarma — Depo · Mağaza ayrımlı; girilen adetler sunucuda taslak olarak otomatik saklanır. */
export default function QuickCountDialog({ open, onOpenChange, onApplied, onAddNew }) {
  const [type, setType] = useState("sayim");
  const [loc, setLoc] = useState("depo");
  const [items, setItems] = useState([]);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState("");
  const [scanOpen, setScanOpen] = useState(false);
  const [askClose, setAskClose] = useState(false);

  useEffect(() => {
    if (!open) { setReady(false); return undefined; }
    let alive = true;
    setSaved("");
    api.get("/stock/count-draft").then(({ data }) => {
      if (!alive) return;
      const list = data?.items || [];
      setType(data?.type || "sayim"); setLoc(data?.location || "depo");
      setItems(list.map((x) => ({ product: x, qty: x.qty, touched: true })));
      if (list.length) toast.info(`Kaydedilmemiş sayım geri yüklendi · ${list.length} ürün`);
    }).catch(() => { if (alive) setItems([]); }).finally(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, [open]);

  const payload = () => ({ type, location: loc, items: items.map((x) => ({ ...snap(x.product), qty: x.qty })) });
  useEffect(() => {   // her değişiklikte taslak sunucuya yazılır (yanlışlıkla kapanma / sayfa yenileme kaybı yok)
    if (!open || !ready) return undefined;
    setSaved(items.length ? "saving" : "");
    const t = setTimeout(() => {
      api.put("/stock/count-draft", payload()).then(() => setSaved(items.length ? "saved" : "")).catch(() => setSaved("error"));
    }, 500);
    return () => clearTimeout(t);
  }, [items, type, loc, open, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  const remap = (t, l) => setItems((list) => list.map((x) => (x.touched ? x : { ...x, qty: qDefault(x.product, t, l) })));
  const pickType = (t) => {
    const l = t === "transfer" ? "magaza" : type === "transfer" ? "depo" : loc;
    setType(t); setLoc(l); remap(t, l);
  };
  const pickLoc = (l) => { setLoc(l); remap(type, l); };
  const add = (p, fromScan = false) => setItems((list) => {
    const i = list.findIndex((x) => x.product.id === p.id);
    if (i >= 0) {
      if (!fromScan || type === "sayim") { toast.info(`${p.name} zaten listede`); return list; }
      const cur = list[i]; const nq = (Number(cur.qty) || 0) + 1;
      toast.info(`${p.name} · ${nq}`);
      return [{ ...cur, qty: nq, touched: true }, ...list.filter((_, k) => k !== i)];
    }
    if (fromScan) toast.success("Eklendi: " + p.name);
    return [{ product: p, qty: qDefault(p, type, loc), touched: false }, ...list];   // son eklenen EN ÜSTTE
  });
  const resolveScan = async (code) => {
    try {
      const { data } = await api.get(`/products/lookup?q=${encodeURIComponent(code)}`);
      const exact = data.find((r) => [r.product.code, r.product.barcode, ...(r.product.barcodes || [])].some((b) => String(b || "").toLowerCase() === String(code).toLowerCase())) || (data.length === 1 ? data[0] : null);
      if (!exact) { toast.error(data.length ? `"${code}" birden fazla ürüne uyuyor — aramadan seçin` : `Barkod sistemde YOK: ${code}`); return { ok: false, label: data.length ? `Birden fazla: ${code}` : `Yok: ${code}` }; }
      add(exact.product, true);
      return { ok: true, label: exact.product.name };
    } catch { toast.error("Arama hatası"); return { ok: false, label: "Arama hatası" }; }
  };
  const setQty = (id, v) => setItems((list) => list.map((x) => (x.product.id === id ? { ...x, qty: v, touched: true } : x)));
  const rm = (id) => setItems((list) => list.filter((x) => x.product.id !== id));

  const requestClose = () => { if (items.length && !busy) setAskClose(true); else if (!busy) onOpenChange(false); };
  const closeKeep = async () => {
    try { await api.put("/stock/count-draft", payload()); toast.success("Taslak kaydedildi — tekrar açınca kaldığınız yerden devam edersiniz"); }
    catch { toast.error("Taslak kaydedilemedi"); }
    setAskClose(false); onOpenChange(false);
  };
  const closeDiscard = async () => {
    try { await api.delete("/stock/count-draft"); } catch { /* yoksay */ }
    setItems([]); setAskClose(false); onOpenChange(false);
  };

  const apply = async () => {
    const all = items.filter((x) => x.product?.id);
    const list = all.filter((x) => String(x.qty ?? "").trim() !== "");   // adedi BOŞ satırlar uygulanmaz (yanlışlıkla sıfırlama/çıkış olmaz)
    const blank = all.length - list.length;
    if (!list.length) return toast.error(all.length ? "Adet girilmiş ürün yok — adetleri yazın" : "Listeye ürün ekleyin");
    const where = type === "transfer" ? (loc === "magaza" ? "Depo → Mağaza aktarma" : "Mağaza → Depo aktarma") : `${LOC_TR[loc]} · ${TYPES.find((t) => t[0] === type)[1]}`;
    const msg = `${where}: ${list.length} ürün güncellenecek.${blank ? ` Adedi boş ${blank} ürün atlanacak (listede kalır).` : ""}${type === "cikis" ? " Bu işlem stoktan ÇIKIŞ yapar." : ""}`;
    if (!await confirm(msg, { title: "Stok işlemini onaylayın", confirmText: "Uygula", destructive: type === "cikis" })) return;
    const note = type === "transfer" ? `Hızlı aktarma · ${loc === "magaza" ? "Depo → Mağaza" : "Mağaza → Depo"}` : `Hızlı ${TYPES.find((t) => t[0] === type)[1].toLocaleLowerCase("tr")} · ${LOC_TR[loc]}`;
    const entries = list.map((x) => ({ product_id: x.product.id, type, quantity: Number(x.qty) || 0, note, location: loc }));
    setBusy(true);
    try {
      const { data } = await api.post("/stock/movements/bulk", { entries });
      try { if (blank) await api.put("/stock/count-draft", { type, location: loc, items: all.filter((x) => !list.includes(x)).map((x) => ({ ...snap(x.product), qty: x.qty })) }); else await api.delete("/stock/count-draft"); } catch { /* yoksay */ }
      toast.success(`${data.count} üründe stok güncellendi (${where})${blank ? ` · ${blank} boş satır taslakta kaldı` : ""}`);
      if (blank) { setItems((cur) => cur.filter((x) => !list.includes(x))); onApplied?.(); return; }
      setItems([]); onOpenChange(false); onApplied?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) requestClose(); }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()} data-testid="quick-count-dialog">
          <DialogHeader>
            <DialogTitle className="font-head text-2xl flex items-center gap-2 flex-wrap"><ClipboardEdit className="h-6 w-6 text-primary" />Hızlı Sayım / Stok Hareketi</DialogTitle>
            <div className="min-h-[16px]"><SaveState state={saved} /></div>
          </DialogHeader>
          {!ready ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div> : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {TYPES.map(([v, lbl, desc, on]) => (
                  <button key={v} type="button" onClick={() => pickType(v)} className={cn("rounded-xl border p-3 text-left transition-colors", type === v ? `${on} ring-1` : "hover:bg-muted/50")} data-testid={`quick-type-${v}`}>
                    <div className="font-bold text-sm">{lbl}</div>
                    <div className="text-[11px] text-muted-foreground">{desc}</div>
                  </button>
                ))}
              </div>
              <div className="space-y-1.5">
                <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{type === "transfer" ? "Aktarma yönü" : "Bölüm"}</div>
                <LocPicker type={type} loc={loc} onPick={pickLoc} />
              </div>
              <div className="space-y-2">
                <BarcodeInput onResolve={resolveScan} onAddNew={onAddNew ? (code) => { setItems((l) => l); onAddNew(code); } : undefined} placeholder="Barkod okut + Enter" autoFocus testid="quick-barcode-input" className="w-full" />
                <div className="flex gap-2 items-center flex-wrap">
                  <div className="flex-1 min-w-[160px]"><ProductPicker label="Ürün Ara / Ekle" size="default" variant="outline" className="w-full justify-start" onSelect={(p) => add(p)} testid="quick-pick" /></div>
                  <Button variant="outline" onClick={() => setScanOpen(true)} data-testid="quick-scan-btn"><ScanLine className="h-4 w-4 mr-2" />Kamera</Button>
                </div>
              </div>
              {items.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground border border-dashed rounded-lg"><ScanLine className="h-8 w-8 mx-auto mb-2 opacity-40" /><p className="font-semibold">Ürün tarayın veya arayıp ekleyin</p><p className="text-sm mt-1">Girdiğiniz adetler otomatik saklanır; pencere kapansa bile kaybolmaz.</p></div>
              ) : (
                <div className="space-y-2" data-testid="quick-items">
                  {items.map((x) => <QuickRow key={x.product.id} x={x} type={type} loc={loc} onQty={setQty} onRemove={rm} />)}
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={requestClose} data-testid="quick-cancel">Kapat</Button>
            <Button onClick={apply} disabled={busy || !items.length || !ready} className={type === "cikis" ? "bg-red-600 hover:bg-red-700" : ""} data-testid="quick-apply">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}Uygula ({items.length} ürün)</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={askClose} onOpenChange={setAskClose}>
        <DialogContent className="max-w-sm" data-testid="quick-close-ask">
          <DialogHeader><DialogTitle className="font-head text-lg">Sayım henüz uygulanmadı</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Listede {items.length} ürün var. Stok değişmedi. Taslağı saklayıp daha sonra kaldığınız yerden devam edebilirsiniz.</p>
          <div className="grid gap-2">
            <Button onClick={() => setAskClose(false)} data-testid="quick-close-continue">Sayıma Devam Et</Button>
            <Button variant="outline" onClick={closeKeep} data-testid="quick-close-keep">Kapat · Taslak Kalsın</Button>
            <Button variant="ghost" className="text-destructive" onClick={closeDiscard} data-testid="quick-close-discard">Taslağı Sil ve Kapat</Button>
          </div>
        </DialogContent>
      </Dialog>

      <BarcodeScanner open={scanOpen} onClose={() => setScanOpen(false)} onDetected={(c) => { setScanOpen(false); resolveScan(c); }} />
    </>
  );
}
